import { normalizePublicScopeIds } from './wrPublicScope';

/** Legacy targets can be JSON arrays, numeric IDs, or exact master names. */
export const parseLegacyPublicScopeTargets = (raw: unknown): string[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.flatMap(parseLegacyPublicScopeTargets);
  const source = String(raw).trim();
  if (!source) return [];
  try {
    const decoded: unknown = JSON.parse(source);
    if (Array.isArray(decoded)) return decoded.flatMap(parseLegacyPublicScopeTargets);
    if (typeof decoded === 'string' && decoded !== source) {
      return parseLegacyPublicScopeTargets(decoded);
    }
  } catch {
    // Plain master codes/names are also valid legacy values.
  }
  const separator = /^\d+(?:\s+\d+)+$/.test(source) ? /\s+/ : /[,，;、\n\r]+/;
  return source.split(separator).map((value) => value.trim()).filter(Boolean);
};

type ScopeTargetOption = {
  id: unknown;
  code?: unknown;
  label?: unknown;
};

/** Never replace a stored target with the current user's organization. */
export const resolvePublicScopeTargets = (
  rawIds: unknown,
  legacy: unknown,
  options: ScopeTargetOption[],
): { ids: string[]; unresolved: string[] } => {
  // A populated ID array is authoritative. Stale legacy names must not add recipients.
  const tokens = Array.isArray(rawIds) && rawIds.length > 0
    ? rawIds.map((value) => String(value ?? '').trim())
    : parseLegacyPublicScopeTargets(legacy);
  const useIdsOnly = Array.isArray(rawIds) && rawIds.length > 0;
  const ids: string[] = [];
  const unresolved: string[] = [];
  const key = (value: unknown) => String(value ?? '').trim().toLocaleLowerCase();
  tokens.forEach((token) => {
    const numericId = normalizePublicScopeIds([token])[0];
    if (numericId) {
      // Preserve IDs even while the master is incomplete or the target is inactive.
      ids.push(numericId);
      return;
    }
    const matches = useIdsOnly ? [] : normalizePublicScopeIds(options
      .filter((option) => [option.code, option.label].some((value) => key(value) === key(token)))
      .map((option) => option.id));
    if (matches.length === 1) ids.push(matches[0]);
    else unresolved.push(token || '(空のID)');
  });
  return { ids: Array.from(new Set(ids)), unresolved: Array.from(new Set(unresolved)) };
};

type ScopeRow = {
  scope_type?: unknown;
  private_flg?: unknown;
  department_id?: unknown;
  scope_department_id?: unknown;
  department_ids?: unknown;
  team_ids?: unknown;
  public_department?: unknown;
  public_team?: unknown;
  scope_warning?: unknown;
  stored_scope?: {
    department_ids?: unknown;
    team_ids?: unknown;
    public_department?: unknown;
    public_team?: unknown;
  } | null;
};

/** Warn about stored contradictions even when the API returns effective IDs. */
export const getDepartmentScopeWarning = (
  row: ScopeRow | null | undefined,
  settingDepartmentId: unknown,
  options: ScopeTargetOption[],
): string | null => {
  if (!row) return null;
  const explicit = String(row.scope_type ?? '').trim().toLowerCase();
  const isDepartment = explicit === 'department' || (!explicit && Number(row.private_flg) === 1 &&
    parseLegacyPublicScopeTargets(row.public_team).length === 0);
  if (!isDepartment) return null;
  const owner = normalizePublicScopeIds([row.scope_department_id ?? row.department_id ?? settingDepartmentId])[0];
  if (!owner) return '課内公開の基準となる課を確認できません。設定対象の課を確認してください。';
  const stored = row.stored_scope ?? row;
  const targets = resolvePublicScopeTargets(stored.department_ids, stored.public_department, options);
  const legacy = resolvePublicScopeTargets([], stored.public_department, options);
  if (row.scope_warning || targets.unresolved.length || legacy.unresolved.length ||
      targets.ids.some(id => id !== owner) || legacy.ids.some(id => id !== owner) ||
      (Array.isArray(stored.team_ids) && stored.team_ids.length > 0) ||
      parseLegacyPublicScopeTargets(stored.public_team).length > 0) {
    return '保存済みの公開先と「課内公開」の設定対象課が一致していません。閲覧範囲は設定対象課として判定します。保存済みデータは自動変更されません。';
  }
  return null;
};

/** Resolve legacy names only when every target has an unambiguous master match. */
export const restoreLegacyWeeklyReportScopeTargets = <T extends ScopeRow>(
  row: T,
  masters: { departments?: any[]; teams?: any[] },
): T => {
  const explicit = String(row.scope_type ?? '').trim().toLowerCase();
  const scope = explicit || (Number(row.private_flg) === 2 ? 'departments'
    : Number(row.private_flg) === 1 ? (parseLegacyPublicScopeTargets(row.public_team).length > 0 ? 'teams' : 'department') : '');
  if (!['teams', 'departments', 'department'].includes(scope)) return row;
  if (scope === 'department') {
    return { ...row, scope_type: scope,
      department_ids: normalizePublicScopeIds([row.scope_department_id ?? row.department_id]).map(Number), team_ids: [] };
  }
  const isTeam = scope === 'teams';
  const sourceDepartment = normalizePublicScopeIds([row.department_id])[0];
  const options = (isTeam ? masters.teams ?? [] : masters.departments ?? [])
    .filter((option) => !isTeam || (sourceDepartment && String(option.department_id ?? option.departmentId) === sourceDepartment))
    .map((option) => ({
      id: isTeam ? option.team_id ?? option.id : option.department_id ?? option.id,
      code: option.value ?? option.code,
      label: option.label ?? option.name,
    }));
  const resolved = resolvePublicScopeTargets(
    isTeam ? row.team_ids : row.department_ids,
    isTeam ? row.public_team : row.public_department,
    options,
  );
  const ids = resolved.unresolved.length === 0 ? resolved.ids.map(Number) : [];
  return { ...row, scope_type: scope, ...(isTeam ? { team_ids: ids } : { department_ids: ids }) };
};
